import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import "./CSS/CustomerCreditPremium.css";
import "./CSS/ExpenseCategoryPremium.css";
import {
  FaCheckCircle,
  FaEdit,
  FaExclamationCircle,
  FaLayerGroup,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaSortAmountDown,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";

const pageSize = 5;

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaLayerGroup;

  return (
    <div
      className={`cc-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="cc-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="cc-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`cc-summary-card ${tone}`}>
      <div className="cc-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="exp-cat-modal-overlay" role="presentation">
      <div className="exp-cat-modal" role="dialog" aria-modal="true" aria-labelledby="exp-cat-modal-title">
        <div className="exp-cat-modal-head">
          <div className="exp-cat-modal-icon">
            <FaTrashAlt aria-hidden="true" />
          </div>
          <div>
            <span>Confirm action</span>
            <h3 id="exp-cat-modal-title">{title}</h3>
          </div>
        </div>
        <p>{message}</p>
        <div className="exp-cat-modal-actions">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function getPageNumbers(activePage, totalPages) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, activePage, activePage - 1, activePage + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

export default function ExpenseCategoryMaster() {
  const token = localStorage.getItem("token");

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({
    id: null,
    name: "",
    code: "",
    parent_id: "",
    is_recurring_default: false,
    is_active: true,
    sort_order: 0,
  });

  const showNotice = (type, title, message) => {
    setNotice({ type, title, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => setNotice(null), 3600);
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/expenses/categories", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data || []);
    } catch (err) {
      showNotice("error", "Load failed", err?.response?.data?.error || "Unable to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetForm = () => {
    setForm({
      id: null,
      name: "",
      code: "",
      parent_id: "",
      is_recurring_default: false,
      is_active: true,
      sort_order: 0,
    });
  };

  const topLevelCount = useMemo(() => categories.filter((row) => !row.parent_id).length, [categories]);
  const activeCount = useMemo(() => categories.filter((row) => Number(row.is_active || 0) === 1).length, [categories]);
  const recurringDefaultCount = useMemo(
    () => categories.filter((row) => Number(row.is_recurring_default || 0) === 1).length,
    [categories]
  );
  const inactiveCount = Math.max(0, categories.length - activeCount);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories.filter((row) => {
      const matchesSearch =
        !q ||
        [row.name, row.code, row.parent_name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && Number(row.is_active || 0) === 1) ||
        (statusFilter === "inactive" && Number(row.is_active || 0) !== 1);
      return matchesSearch && matchesStatus;
    });
  }, [categories, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const rows = filteredCategories.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const canSave = form.name.trim().length > 0;

  const handleSave = async () => {
    const name = form.name.trim();
    const code = form.code.trim();

    if (!name) {
      showNotice("warning", "Name required", "Please enter an expense category name.");
      return;
    }
    if (form.sort_order !== "" && Number.isNaN(Number(form.sort_order))) {
      showNotice("warning", "Sort order invalid", "Sort order should be a number.");
      return;
    }
    if (form.parent_id && String(form.parent_id) === String(form.id)) {
      showNotice("warning", "Parent invalid", "A category cannot be its own parent.");
      return;
    }

    const payload = {
      name,
      code: code || null,
      parent_id: form.parent_id || null,
      is_recurring_default: !!form.is_recurring_default,
      is_active: !!form.is_active,
      sort_order: Number(form.sort_order || 0),
    };

    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/api/expenses/categories/${form.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Category updated", "The category has been saved.");
      } else {
        await api.post("/api/expenses/categories", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Category added", "The new category is ready.");
      }
      resetForm();
      await fetchCategories();
    } catch (err) {
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save category.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || "",
      code: row.code || "",
      parent_id: row.parent_id || "",
      is_recurring_default: row.is_recurring_default === 1 || row.is_recurring_default === true,
      is_active: row.is_active === 1 || row.is_active === true,
      sort_order: row.sort_order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggleActive = async (row) => {
    try {
      const payload = {
        name: row.name,
        code: row.code,
        parent_id: row.parent_id,
        is_recurring_default: row.is_recurring_default,
        is_active: Number(row.is_active || 0) === 1 ? 0 : 1,
        sort_order: row.sort_order ?? 0,
      };

      await api.put(`/api/expenses/categories/${row.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotice("success", "Status updated", `Category is now ${Number(row.is_active || 0) === 1 ? "inactive" : "active"}.`);
      await fetchCategories();
    } catch (err) {
      showNotice("error", "Update failed", err?.response?.data?.error || "Unable to update category status.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/expenses/categories/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotice("success", "Category deleted", "The category was removed.");
      await fetchCategories();
    } catch (err) {
      showNotice("error", "Delete failed", err?.response?.data?.error || "Unable to delete category.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const parentOptions = categories.filter((row) => !form.id || row.id !== form.id);

  return (
    <div className="cc-page text-light exp-cat-page">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete expense category?"
        message="This removes the category from the master list. If it is already used in expenses, the server may block it."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <section className="cc-hero">
        <div>
          <div className="cc-eyebrow">Expense Master</div>
          <h2>Expense categories with a calmer, cleaner admin feel.</h2>
          <p>
            Keep categories easy to scan, quick to edit, and safe on mobile so the master data stays
            friendly for day-to-day work.
          </p>
          <div className="cc-hero-meta">
            <span>{categories.length} categories</span>
            <span>{activeCount} active</span>
            <span>{recurringDefaultCount} recurring defaults</span>
          </div>
        </div>

        <div className="cc-hero-actions">
          <button type="button" className="btn cc-secondary-action" onClick={fetchCategories} disabled={loading}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button
            type="button"
            className="btn cc-primary-action"
            onClick={() => {
              resetForm();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <FaPlus aria-hidden="true" />
            New category
          </button>
        </div>
      </section>

      <section className="cc-summary-grid">
        <StatCard
          tone="blue"
          icon={FaLayerGroup}
          label="Total categories"
          value={String(categories.length)}
          caption="All expense category rows"
        />
        <StatCard
          tone="green"
          icon={FaCheckCircle}
          label="Active"
          value={String(activeCount)}
          caption="Visible for expense entry"
        />
        <StatCard
          tone="amber"
          icon={FaSortAmountDown}
          label="Top-level"
          value={String(topLevelCount)}
          caption="Categories without parent"
        />
        <StatCard
          tone="slate"
          icon={FaEdit}
          label="Inactive"
          value={String(inactiveCount)}
          caption="Available for future reactivation"
        />
      </section>

      <section className="cc-context-strip">
        <div className="cc-context-head">
          <div>
            <span>Category context</span>
            <h3>Search first, then edit the master list</h3>
          </div>
          <div className="cc-context-chip">
            <strong>{statusFilter === "all" ? "All statuses" : statusFilter === "active" ? "Active only" : "Inactive only"}</strong>
            <span>{filteredCategories.length} rows in view</span>
          </div>
        </div>

        <div className="exp-cat-filter-grid">
          <div className="cc-search-shell">
            <FaSearch aria-hidden="true" />
            <input
              className="form-control"
              placeholder="Search category or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>
      </section>

      <section className="cc-grid exp-cat-grid">
        <div className="cc-card cc-form-card">
          <div className="cc-card-head">
            <div>
              <span>Category form</span>
              <h3>{form.id ? "Edit category details" : "Add a new expense category"}</h3>
            </div>
            <div className="cc-ledger-chip">
              <strong>Keep it simple</strong>
              <span>One clean master row</span>
            </div>
          </div>

          <div className="cc-form-grid exp-cat-form-grid">
            <div>
              <label className="form-label">Category name</label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Eg: Electricity, Rent, Salary"
              />
            </div>
            <div>
              <label className="form-label">Code</label>
              <input
                type="text"
                className="form-control"
                value={form.code}
                onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                placeholder="ELEC, RENT, SAL"
              />
            </div>
            <div>
              <label className="form-label">Parent category</label>
              <select
                className="form-select"
                value={form.parent_id}
                onChange={(e) => setForm((prev) => ({ ...prev, parent_id: e.target.value }))}
              >
                <option value="">No parent</option>
                {parentOptions.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Sort order</label>
              <input
                type="number"
                className="form-control"
                value={form.sort_order}
                onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
              />
            </div>
            <div className="exp-cat-switch-row">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={form.is_recurring_default}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_recurring_default: e.target.checked }))}
                />
                <span>Recurring default</span>
              </label>
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
                <span>Active</span>
              </label>
            </div>
          </div>

          <div className="cc-action-row">
            <button type="button" className="btn cc-primary-action" onClick={handleSave} disabled={saving || !canSave}>
              <FaSave aria-hidden="true" />
              {saving ? "Saving..." : form.id ? "Update category" : "Save category"}
            </button>
            {form.id && (
              <button type="button" className="btn btn-light" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </div>

        <div className="cc-card cc-form-card">
          <div className="cc-card-head">
            <div>
              <span>Category insights</span>
              <h3>Small summary, enough to orient the admin screen</h3>
            </div>
            <div className="cc-table-meta">
              <span>Parent groups</span>
              <strong>{topLevelCount}</strong>
            </div>
          </div>

          <div className="exp-cat-insights">
            <div className="exp-cat-note">
              <strong>Recurring-ready</strong>
              <span>{recurringDefaultCount} categories marked for recurring use</span>
            </div>
            <div className="exp-cat-note">
              <strong>Search friendly</strong>
              <span>Quick lookup works by category name or code</span>
            </div>
            <div className="exp-cat-note">
              <strong>Safe parenting</strong>
              <span>Parent category choices always exclude the current row</span>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-card cc-report-card">
        <div className="cc-card-head">
          <div>
            <span>Category register</span>
            <h3>Compact master table with mobile-friendly actions</h3>
          </div>
          <div className="cc-table-meta">
            <span>{filteredCategories.length} rows</span>
            <strong>
              {safePage}/{totalPages}
            </strong>
          </div>
        </div>

        <div className="table-responsive cc-table-wrap">
          <table className="table cc-table">
            <thead>
              <tr>
                <th style={{ color: "#fff", background: "#0f172a" }}>Name</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Code</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Parent</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Sort</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Recurring</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Status</th>
                <th style={{ width: 220, color: "#fff", background: "#0f172a" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td data-label="Name">
                    <strong>{row.name}</strong>
                    <span>{row.parent_name || "Top-level"}</span>
                  </td>
                  <td data-label="Code">{row.code || "-"}</td>
                  <td data-label="Parent">{row.parent_name || "-"}</td>
                  <td data-label="Sort">{row.sort_order ?? 0}</td>
                  <td data-label="Recurring">{Number(row.is_recurring_default || 0) ? "Yes" : "No"}</td>
                  <td data-label="Status">
                    <span className={`cc-pill ${Number(row.is_active || 0) ? "paid" : "pending"}`}>
                      {Number(row.is_active || 0) ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td data-label="Actions" className="cc-row-actions exp-cat-actions">
                    <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => handleEdit(row)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm cc-mini-btn ${Number(row.is_active || 0) ? "btn-outline-danger" : "btn-outline-success"}`}
                      onClick={() => handleToggleActive(row)}
                    >
                      {Number(row.is_active || 0) ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger cc-mini-btn"
                      onClick={() => setDeleteTarget(row)}
                    >
                      Del
                    </button>
                  </td>
                </tr>
              ))}

              {!rows.length && (
                <tr>
                  <td colSpan="7">
                    <div className="cc-empty-state">
                      <FaLayerGroup aria-hidden="true" />
                      <strong>No categories found</strong>
                      <span>Try another search or add the first expense category.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredCategories.length > pageSize && (
          <div className="cc-pagination">
            <button
              type="button"
              className="cc-page-btn"
              disabled={safePage === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <div className="cc-page-list">
              {getPageNumbers(safePage, totalPages).map((pageNo) => (
                <button
                  key={pageNo}
                  type="button"
                  className={`cc-page-btn ${safePage === pageNo ? "active" : ""}`}
                  onClick={() => setPage(pageNo)}
                >
                  {pageNo}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cc-page-btn"
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
